import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  DashboardDataTableScroll,
  DashboardDataTableTable,
  dashboardDataTable as dt,
} from "src/components/common";
import RestaurantSectionCard from "./RestaurantSectionCard";
import RestaurantKpiCard from "./RestaurantKpiCard";
import RestaurantCategoryBars from "./charts/RestaurantCategoryBars";
import { ABANDON_REASONS, CART_FUNNEL, RECOVERY_CAMPAIGNS } from "../data/restaurantDemoData";
import { useRestaurantFormatters } from "../utils/restaurantFormatters";
import type { RestaurantMetrics } from "../utils/restaurantMetrics";

const FUNNEL_STAGE_LABEL_KEYS: Record<string, string> = {
  cart_created: "restaurantAnalytics.cart.stageCartCreated",
  cart_with_items: "restaurantAnalytics.cart.stageCartWithItems",
  checkout_started: "restaurantAnalytics.cart.stageCheckoutStarted",
  payment_attempted: "restaurantAnalytics.cart.stagePaymentAttempted",
  order_placed: "restaurantAnalytics.cart.stageOrderPlaced",
};

const ABANDON_REASON_LABEL_KEYS: Record<string, string> = {
  delivery_fee: "restaurantAnalytics.cart.reasonDeliveryFee",
  long_wait: "restaurantAnalytics.cart.reasonLongWait",
  payment_failed: "restaurantAnalytics.cart.reasonPaymentFailed",
  changed_mind: "restaurantAnalytics.cart.reasonChangedMind",
  app_issue: "restaurantAnalytics.cart.reasonAppIssue",
  price_comparison: "restaurantAnalytics.cart.reasonPriceComparison",
};

const CAMPAIGN_LABEL_KEYS: Record<string, string> = {
  push_15min: "restaurantAnalytics.cart.campaignPush15Min",
  sms_1hr: "restaurantAnalytics.cart.campaignSms1Hr",
  whatsapp_3hr: "restaurantAnalytics.cart.campaignWhatsapp3Hr",
  email_24hr: "restaurantAnalytics.cart.campaignEmail24Hr",
  discount_48hr: "restaurantAnalytics.cart.campaignDiscount48Hr",
};

interface RestaurantAbandonedCartAnalyticsProps {
  metrics: RestaurantMetrics;
  showKpis?: boolean;
}

const RestaurantAbandonedCartAnalytics: FC<RestaurantAbandonedCartAnalyticsProps> = ({
  metrics,
  showKpis = true,
}) => {
  const { t } = useTranslation();
  const fmt = useRestaurantFormatters();

  const scale = metrics.cart.abandonedCarts / Math.max(CART_FUNNEL[0].value * metrics.cart.abandonRate, 1);
  const firstStageValue = CART_FUNNEL[0].value;

  const reasonData = ABANDON_REASONS.map(reason => ({
    id: reason.id,
    label: t(ABANDON_REASON_LABEL_KEYS[reason.id] ?? reason.id, { defaultValue: reason.id }),
    value: Math.round(metrics.cart.abandonedCarts * reason.share),
  }));

  return (
    <section className="restaurant-abandoned-cart">
      <h2 className="restaurant-abandoned-cart__title">
        {t("restaurantAnalytics.cart.title", { defaultValue: "Abandoned cart analytics" })}
      </h2>

      {showKpis ? (
        <div className="restaurant-analytics-dashboard__kpi-row">
          <RestaurantKpiCard
            label={t("restaurantAnalytics.cart.kpiAbandonRate", { defaultValue: "Abandonment rate" })}
            value={fmt.formatPercent(metrics.cart.abandonRate)}
            accent="warning"
            tooltip={t("restaurantAnalytics.kpiTooltips.abandonRate", { defaultValue: "Share of started carts that were abandoned before checkout completed." })}
          />
          <RestaurantKpiCard
            label={t("restaurantAnalytics.cart.kpiLostRevenue", { defaultValue: "Estimated lost revenue" })}
            value={fmt.formatCurrency(metrics.cart.lostRevenue, { compact: true })}
            accent="destructive"
            tooltip={t("restaurantAnalytics.kpiTooltips.lostRevenue", { defaultValue: "Estimated revenue lost to abandoned carts in the period." })}
          />
          <RestaurantKpiCard
            label={t("restaurantAnalytics.cart.kpiRecoveredOrders", { defaultValue: "Recovered orders" })}
            value={fmt.formatNumber(metrics.cart.recoveredOrders)}
            accent="success"
            tooltipAlign="end"
            tooltip={t("restaurantAnalytics.kpiTooltips.recoveredOrders", { defaultValue: "Orders completed after a recovery campaign reached the customer." })}
          />
          <RestaurantKpiCard
            label={t("restaurantAnalytics.cart.kpiRecoveryRate", { defaultValue: "Recovery rate" })}
            value={fmt.formatPercent(metrics.cart.recoveryRate)}
            accent="success"
            tooltipAlign="end"
            tooltip={t("restaurantAnalytics.kpiTooltips.recoveryRate", { defaultValue: "Share of abandoned carts recovered through follow-up campaigns." })}
          />
        </div>
      ) : null}

      <div className="restaurant-analytics-dashboard__grid restaurant-analytics-dashboard__grid--two">
        <RestaurantSectionCard
          title={t("restaurantAnalytics.cart.funnelTitle", { defaultValue: "Checkout & recovery funnel" })}
          tip={t("restaurantAnalytics.chartTips.cartFunnel", { defaultValue: "Drop-off at each checkout stage, from cart created through order placed." })}
        >
          <div className="restaurant-cart-funnel">
            {CART_FUNNEL.map((stage, index) => {
              const value = stage.value * scale;
              const previousValue = index > 0 ? CART_FUNNEL[index - 1].value * scale : value;
              const conversion = previousValue > 0 ? value / previousValue : 1;
              const width = (stage.value / firstStageValue) * 100;
              return (
                <div key={stage.id} className="restaurant-cart-funnel__stage">
                  <div className="restaurant-cart-funnel__stage-head">
                    <span className="restaurant-cart-funnel__stage-name">
                      {t(FUNNEL_STAGE_LABEL_KEYS[stage.id] ?? stage.id, { defaultValue: stage.id })}
                    </span>
                    <span className="restaurant-cart-funnel__stage-value">
                      {`${fmt.formatNumber(Math.round(value))} – ${fmt.formatPercent(conversion, 0)}`}
                    </span>
                  </div>
                  <div className="restaurant-cart-funnel__track">
                    <div
                      className="restaurant-cart-funnel__fill"
                      style={{ width: `${width}%`, opacity: 1 - index * 0.12 }}
                    />
                  </div>
                </div>
              );
            })}
            <p className="restaurant-cart-funnel__footer">
              {`${t("restaurantAnalytics.cart.cartCreatedLabel", { defaultValue: "Cart created" })}: ${fmt.formatNumber(Math.round(firstStageValue * scale))}`}
            </p>
          </div>
        </RestaurantSectionCard>

        <RestaurantCategoryBars
          title={t("restaurantAnalytics.cart.reasonsTitle", { defaultValue: "Abandonment reasons" })}
          tip={t("restaurantAnalytics.chartTips.cartReasons", { defaultValue: "Why customers abandoned their carts, by estimated cart count." })}
          data={reasonData}
          valueKind="number"
          colorIndex={2}
        />
      </div>

      <RestaurantSectionCard
        title={t("restaurantAnalytics.cart.campaignsTitle", { defaultValue: "Recovery campaigns" })}
        tip={t("restaurantAnalytics.chartTips.cartCampaigns", { defaultValue: "Messages sent, orders recovered, and recovery rate per follow-up campaign." })}
        bodyClassName="restaurant-table-body"
      >
        <DashboardDataTableScroll>
          <DashboardDataTableTable className={`${dt.tableCompact} restaurant-wide-table`}>
            <thead>
              <tr>
                <th>{t("restaurantAnalytics.cart.columnCampaign", { defaultValue: "Campaign" })}</th>
                <th className={dt.number}>
                  {t("restaurantAnalytics.cart.columnSent", { defaultValue: "Messages sent" })}
                </th>
                <th className={dt.number}>
                  {t("restaurantAnalytics.cart.columnRecoveredOrders", { defaultValue: "Recovered orders" })}
                </th>
                <th className={dt.number}>
                  {t("restaurantAnalytics.cart.columnRecoveredRevenue", { defaultValue: "Recovered revenue" })}
                </th>
                <th className={dt.number}>
                  {t("restaurantAnalytics.cart.columnRecoveryRate", { defaultValue: "Recovery rate" })}
                </th>
              </tr>
            </thead>
            <tbody>
              {RECOVERY_CAMPAIGNS.map(campaign => {
                const sent = Math.round(campaign.sent * scale);
                const recovered = Math.round(campaign.recovered * scale);
                const revenue = Math.round(campaign.revenue * scale);
                return (
                  <tr key={campaign.id}>
                    <td className={dt.cellEmphasis}>
                      {t(CAMPAIGN_LABEL_KEYS[campaign.id] ?? campaign.id, { defaultValue: campaign.id })}
                    </td>
                    <td className={dt.number}>{fmt.formatNumber(sent)}</td>
                    <td className={dt.number}>{fmt.formatNumber(recovered)}</td>
                    <td className={dt.number}>{fmt.formatCurrency(revenue, { compact: true })}</td>
                    <td className={`${dt.number} ${dt.metric} ${dt.metricSuccess}`}>
                      {fmt.formatPercent(sent > 0 ? recovered / sent : 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
      </RestaurantSectionCard>
    </section>
  );
};

export default RestaurantAbandonedCartAnalytics;
